/**
 * Token generation and verification utility
 * Simple token-based authentication using JWT-like structure
 */

const TOKEN_SECRET = 'valence-erp-secret-key-change-in-production'; // In production, use env variable
const TOKEN_EXPIRY_HOURS = 24; // Token expires in 24 hours

/**
 * Generate a simple token from user data
 * Format: base64(JSON({userId, uniqueId, role, category, expiresAt}))
 */
export const generateToken = (user) => {
  const payload = {
    userId: user.id,
    uniqueId: user.unique_id,
    role: user.role,
    category: user.category,
    name: user.name,
    projectId: user.project_id,
    clusterId: user.cluster_id,
    teamId: user.team_id,
    expiresAt: new Date(Date.now() + TOKEN_EXPIRY_HOURS * 60 * 60 * 1000).toISOString(),
    issuedAt: new Date().toISOString()
  };

  // Simple base64 encoding (in production, use proper JWT library)
  const token = btoa(JSON.stringify(payload));
  return token;
};

/**
 * Verify and decode token
 * Returns user data if valid, null if invalid/expired
 */
export const verifyToken = (token) => {
  try {
    if (!token) return null;

    const decoded = JSON.parse(atob(token));
    
    // Check if token is expired
    if (new Date(decoded.expiresAt) < new Date()) {
      return null; // Token expired
    }

    return {
      id: decoded.userId,
      unique_id: decoded.uniqueId,
      role: decoded.role,
      category: decoded.category,
      name: decoded.name,
      project_id: decoded.projectId,
      cluster_id: decoded.clusterId,
      team_id: decoded.teamId
    };
  } catch (error) {
    console.error('Token verification error:', error);
    return null;
  }
};

/**
 * Store token in localStorage
 */
export const storeToken = (token) => {
  localStorage.setItem('authToken', token);
};

/**
 * Get token from localStorage
 */
export const getToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Remove token from localStorage
 */
export const removeToken = () => {
  localStorage.removeItem('authToken');
};

/**
 * Check if user has required role
 */
export const hasRole = (user, requiredRoles) => {
  if (!user || !user.role) return false;
  if (Array.isArray(requiredRoles)) {
    return requiredRoles.includes(user.role);
  }
  return user.role === requiredRoles;
};

/**
 * Check if user has required category
 */
export const hasCategory = (user, requiredCategory) => {
  if (!user || !user.category) return false;
  return user.category === requiredCategory;
};
